import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Hub } from 'aws-amplify/utils';
import {
  AuthError,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
} from 'aws-amplify/auth';
import type { AuthenticatedUser } from '@/types/user';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  user: AuthenticatedUser | null;
  status: AuthStatus;
  isLoading: boolean;
  isAuthenticated: boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');

  const fetchUser = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      if (!session.tokens) throw AuthError;

      const [currentUser, userAttributes] = await Promise.all([
        getCurrentUser(),
        fetchUserAttributes(),
      ]);

      const groups = session.tokens.accessToken.payload[
        'cognito:groups'
      ] as Array<string>;

      const authenticatedUser: AuthenticatedUser = {
        ...currentUser,
        ...userAttributes,
        isAdmin: groups?.includes('ADMIN') ?? false,
      };

      setUser(authenticatedUser);
      setStatus('authenticated');
    } catch {
      setUser(null);
      setStatus('unauthenticated');
    }
  }, []);

  useEffect(() => {
    void fetchUser();

    const cancel = Hub.listen('auth', async (data) => {
      const { event } = data.payload;
      if (event === 'signedIn') {
        await fetchUser();
      } else if (event === 'signedOut' || event === 'tokenRefresh_failure') {
        setUser(null);
        setStatus('unauthenticated');
      }
    });

    return () => cancel();
  }, [fetchUser]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      status,
      isLoading: status === 'loading',
      isAuthenticated: status === 'authenticated',
      refreshUser: fetchUser,
    }),
    [user, status, fetchUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
